import React, { useState, useEffect, FocusEvent } from 'react';
import styled, { css } from 'styled-components';
import { Search } from '@mui/icons-material';
import { Input } from '@mui/material';
import ClearAdornment from 'components/UI/ClearAdornment';
import useKeyboardListeners from 'hooks/useKeyboardListeners';
import { Background, Border, Main, Text } from 'styles/themeColors';
import { Space } from 'styles/themeSpaces';
import { Height } from 'styles/themeHeights';
import { BorderRadius } from 'styles/themeBorderRadius';

const SearchContainer = styled.div<any>`
  display: flex;
  align-items: center;
  background-color: ${Background.white};
  border: 1px solid ${Border.main};
  border-radius: ${BorderRadius.m};
  width: ${({ $containerWidth }) => $containerWidth};
  height: ${Height.m};

  ${({ $focused }) => $focused && css`
    border: 1px solid ${Main.brandLight};
    box-shadow: 0 0 3px ${Main.brandLight};
  `}
  
  &:hover {
    border: 1px solid ${Main.brandLight};
  }
`;

const StyledInput = styled(Input) <any>`
  width: ${({ $inputWidth }) => $inputWidth};
  border: ${({ $inputBorder }) => $inputBorder};
  border-radius: ${BorderRadius.m};
  height: 100%;

  #search-bar {
    padding: ${Space.spBase};
  }
`;

SearchContainer.displayName = 'SearchContainer';
StyledInput.displayName = 'StyledInput';

interface Props {
  placeholder?: string;
  searchValue: string;
  inputBorder?: string;
  inputWidth?: string;
  containerWidth?: string;
  onSearch: (searchValue: string) => void;
};

const SearchBar = ({
  containerWidth,
  placeholder = 'Search',
  searchValue,
  inputBorder = 'none',
  inputWidth = '100%',
  onSearch
}: Props) => {
  const [inputFocused, setInputFocused] = useState(false);
  const [value, setValue] = useState(searchValue);

  useEffect(() => {
    setValue(searchValue);
  }, [searchValue]);

  const onClear = () => {
    onSearch('');
    setValue('');
  };
  const onSearchHandler = () => onSearch(value);

  useKeyboardListeners({ enable: inputFocused, onEsc: onClear, onEnter: onSearchHandler });

  /* 
    Transient props are a new pattern to pass props that are explicitly consumed only by styled components 
    and are not meant to be passed down to deeper component layers.
    Example: $height (dollar sign)
  */
  return (
    <SearchContainer
      onFocus={() => setInputFocused(true)}
      onBlur={() => setInputFocused(false)}
      $containerWidth={containerWidth}
      $focused={inputFocused}
    >
      <StyledInput
        id="search-bar"
        value={value}
        onChange={(event: FocusEvent<HTMLInputElement>) => setValue(event.target.value)}
        onBlur={onSearchHandler}
        disableUnderline
        endAdornment={<ClearAdornment onClear={onClear} show={value.length > 0} />}
        placeholder={placeholder}
        $inputBorder={inputBorder}
        $inputWidth={inputWidth}
      />
      <Search style={{ height: Height.s, width: '24px', color: Text.primary, marginRight: 7, zIndex: 100, cursor: 'pointer' }} onClick={onSearchHandler} />
    </SearchContainer>
  )
};

export default SearchBar;
